import type React from 'react';
import classes from './shift-tips-modal.module.scss';
import { sections,rolePeriodLabel } from './shift-tips-modal.utils';
import { useShiftTipsModalController } from './useShiftTipsModalController';
import type { ShiftTipAllocationMode } from 'shared-models';

export const ShiftTipsModal: React.FC<{ date: string; onClose: () => void }> = ({ date, onClose }) => {
  const {
    tips,
    source,
    busy,
    error,
    onCalculateTip,
    onToggleTipLock,
    onClearCalculation,
    overrideEnabled, amounts, wholeAmounts, policyOutdated, onToggleOverride, onChangeOverride, onRefresh,
    allocationMode,ownSource,canCalculate,onChangeMode,onRoleCorrection,
    rolePeriods,hasMoreRolePeriods,onMoreRolePeriods,
  } = useShiftTipsModalController(date);

  return (
    <div
      className="modalOverlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`modalCard ${classes.tipModal}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tip-modal-title"
        dir="rtl"
      >
        <div className="modalHeader">
          <div>
            <h2 id="tip-modal-title">חלוקת טיפים</h2>
            <p>{date}</p>
          </div>
          <button type="button" className="modalClose" aria-label="סגירה" onClick={onClose}>
            ×
          </button>
        </div>
        {error && <p className="errorText">{error}</p>}
        <div className={classes.tipSections}>
          {sections.filter((section) => section.type === 'daily' || tips.some((tip) => tip.typeShift === section.type)).map((section) => {
            const tip = tips.find((item) => item.typeShift === section.type);
            return (
              <section className={classes.tipSection} key={section.type}>
                <div className={classes.tipSectionHeader}>
                  <h3>{section.label}{section.type !== 'daily' ? ' - היסטוריה' : ''}</h3>
                  {tip?.isLocked && <span className="reportStatus yes">נעול</span>}
                  {tip?.sourceStale && <span className="reportStatus no">נתוני הקופה השתנו</span>}
                  {tip && tip.allocationSource !== 'pos' && <span className="reportStatus no">רישום קודם</span>}
                </div>
                {section.type === 'daily' && <div className={classes.tipAmountGrid}>
                  <label>מזומן<output aria-label="מזומן">₪{source?.tipCash ?? '-'}</output></label>
                  <label>אשראי<output aria-label="אשראי">₪{source?.tipCredit ?? '-'}</output></label>
                  <label>אמצעים אחרים<output aria-label="אמצעים אחרים">₪{source?.tipOther ?? '-'}</output></label>
                  <label>שעות בפועל<output aria-label="שעות בפועל">{allocationMode==='shared' && source ? (source.workedMinutes / 60).toFixed(2) : '-'}</output></label>
                  <label>סה״כ מהקופה<output aria-label="סה״כ מהקופה">₪{source ? Number(source.tipCash) + Number(source.tipCredit) + Number(source.tipOther) : '-'}</output></label>
                </div>}
                {section.type === 'daily' && <>
                  <label className={classes.overrideToggle}>שיטת חלוקה<select aria-label="שיטת חלוקה" value={allocationMode}
                    disabled={Boolean(busy) || !source || tip?.isLocked} onChange={event => onChangeMode(event.currentTarget.value as ShiftTipAllocationMode)}>
                    <option value="shared">חלוקה יומית משותפת</option><option value="own">לפי דוח מלצר בקופה</option>
                  </select></label>
                  {allocationMode==='own' && !ownSource?.canCalculate && <p role="alert" className="errorText">יש לרענן את דוח המלצרים בקופה לפני החישוב.</p>}
                  {allocationMode==='shared' && <>
                  {Boolean(source?.unresolvedRoleCount) && <p role="alert" className="errorText">יש להשלים תפקידים חסרים או לא חד-משמעיים לפני החישוב.</p>}
                  {source?.roleSourceOverflow && <p role="alert" className="errorText">נתוני התפקידים חורגים מהכמות הנתמכת.</p>}
                  {Boolean(source?.rolePeriods?.length) && <div className={classes.tipAmountGrid}>
                    {rolePeriods.map(period => <label key={period.attendanceId}>{rolePeriodLabel(period)}
                      <select aria-label={`תפקיד ${period.employeeName} ${period.attendanceId}`}
                        value={period.source==='manager' ? String(period.rolePk) : ''}
                        disabled={Boolean(busy) || tip?.isLocked || policyOutdated || source?.roleSourceOverflow}
                        onChange={event => {const value=event.currentTarget.value;void onRoleCorrection(period.attendanceId,value==='' ? null : Number(value));}}>
                        <option value="">{period.source==='published' ? `אוטומטי: ${period.roleName}` : 'לפי הסידור שפורסם'}</option>
                        {source?.roleOptions?.map(role => <option key={role.id} value={role.id}>{role.sectionName} / {role.shiftName} / {role.name}</option>)}
                      </select>
                    </label>)}
                  </div>}
                  {hasMoreRolePeriods && <button type="button" className="secondaryAction" onClick={onMoreRolePeriods}>הצגת רישומי נוכחות נוספים</button>}
                  <label className={classes.overrideToggle}><input type="checkbox" checked={overrideEnabled}
                    disabled={Boolean(busy) || !source || tip?.isLocked} onChange={event => onToggleOverride(event.currentTarget.checked)} />שינוי סכומי החלוקה</label>
                  {overrideEnabled && <div className={classes.tipAmountGrid}>
                    {([['tipCash', 'מזומן לחלוקה'], ['tipCredit', 'אשראי לחלוקה'], ['tipOther', 'אחר לחלוקה']] as const).map(([key, label]) =>
                      <label key={key}>{label}<input type="number" min="0" step="1" value={amounts[key]}
                        disabled={Boolean(busy) || tip?.isLocked} aria-label={label}
                        onContextMenu={event => event.preventDefault()} onSelect={event => event.preventDefault()}
                        style={{ userSelect: 'none', WebkitTouchCallout: 'none' }}
                        onChange={event => onChangeOverride(key, event.currentTarget.value)} /></label>)}
                  </div>}
                  <p>סה״כ לחלוקה: ₪{wholeAmounts ? Number(amounts.tipCash) + Number(amounts.tipCredit) + Number(amounts.tipOther) : '-'}</p>
                  {!wholeAmounts && <p className="errorText">יש להזין סכומי חלוקה בשקלים שלמים.</p>}
                  </>}
                  {allocationMode==='own' && <p>סה״כ לפי מלצרים: ₪{ownSource ? Number(ownSource.tipCash)+Number(ownSource.tipCredit)+Number(ownSource.tipOther) : '-'}</p>}
                  {policyOutdated && <p role="status">הגדרות הטיפים השתנו. <button type="button" title="רענון" aria-label="רענון הגדרות טיפים" onClick={onRefresh}>↻</button></p>}
                </>}
                {section.type === 'daily' && allocationMode==='shared' && Boolean(source?.openAttendanceCount) && <p className="errorText">קיימים רישומי נוכחות פתוחים בקופה.</p>}
                <div className={classes.tipActions}>
                  {section.type === 'daily' && <button
                    type="button"
                    className="primaryAction"
                    disabled={Boolean(busy) || !source || !canCalculate || tip?.isLocked || (allocationMode==='shared' && !wholeAmounts) || policyOutdated}
                    onClick={() => void onCalculateTip()}
                  >
                    חישוב חלוקה
                  </button>}
                  {tip?.calculatedAt && (
                    <button
                      type="button"
                      className="secondaryAction"
                      disabled={Boolean(busy) || (!tip.isLocked && Boolean(tip.sourceStale))}
                      onClick={() => void onToggleTipLock(tip)}
                    >
                      {tip.isLocked ? 'פתיחת נעילה' : 'נעילת חישוב'}
                    </button>
                  )}
                  {tip?.calculatedAt && !tip.isLocked && (
                    <button
                      type="button"
                      className="secondaryAction"
                      disabled={Boolean(busy)}
                      onClick={() => void onClearCalculation(tip)}
                    >
                      ניקוי חישוב
                    </button>
                  )}
                </div>
                {tip?.distributions.length ? (
                  <div className={classes.tipResults}>
                    <div className={classes.tipResultHead}>
                      <span>עובד</span>
                      <span>דקות</span>
                      <span>טיפ בסיס</span>
                      <span>השלמה</span>
                      <span>טיפ סופי</span>
                    </div>
                    {tip.distributions.map((row) => (
                      <div key={row.id}>
                        <span data-label="עובד">
                          {row.employeeName}
                          <small>
                            {row.shiftName} · {row.roleName}
                          </small>
                        </span>
                        <span data-label="דקות">{row.workedMinutes ?? '-'}</span>
                        <span data-label="טיפ בסיס">₪{Number(row.baseTip)}</span>
                        <span data-label="השלמה">₪{Number(row.completion)}</span>
                        <strong data-label="טיפ סופי">₪{Number(row.finalTip)}</strong>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="reportsState">טרם נשמר חישוב לאזור זה.</p>
                )}
              </section>
            );
          })}
        </div>
      </section>
    </div>
  );
};
